import { useArrMrr } from "@/services/context/ArrMrrContext";
import {
  avatarColorIndex,
  salesPipelineColors
} from "@/components/layouts/SalesPipeline/SalesColors";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { StageData } from "@/entities/reporting/types";
import { displayTotalCurrency, reducePrecision } from "@/utils/numbers";
import React, { useMemo } from "react";
import { entries, sortBy } from "lodash";

const MIN_BAR_HEIGHT_PX = 36;
const CONTAINER_HEIGHT_PX = 192;

interface Props {
  stagesData: StageData[];
  index: number;
  trigger: string | JSX.Element;
  isArr: boolean;
  isProbabilityMode: boolean;
}

const MRRTooltip: React.FC<Props> = ({
  stagesData,
  index,
  trigger,
  isProbabilityMode
}) => {
  const { label: arrMrrLabel } = useArrMrr();
  const data = useMemo(() => stagesData?.[index], [stagesData, index]);

  const stageContributorValue = useMemo(
    () => (stageIndex: number, userId: string) => {
      const stage = stagesData?.[stageIndex];
      const contributor = stage?.contributors?.[userId];
      if (!contributor) return 0;
      return isProbabilityMode
        ? (contributor.weightedValue ?? 0)
        : (contributor.value ?? 0);
    },
    [stagesData, isProbabilityMode]
  );

  const tooltipContent = useMemo(() => {
    if (!data?.contributors) return null;

    const contributors = sortBy(
      entries(stagesData[index].contributors).map(([userId, value]) => ({
        userId,
        ...value,
        valueToUse: isProbabilityMode
          ? (value.weightedValue ?? 0)
          : (value.value ?? 0)
      })),
      "valueToUse"
    );

    // If no contributors, return empty div
    if (contributors.length === 0) {
      return <div className="text-center py-4">No data to display</div>;
    }

    // Calculate total value for percentages
    const totalValueToUse = isProbabilityMode
      ? (data.totalWeightedValue ?? 0)
      : (data.totalValue ?? 0);

    // Calculate metrics for contributors with non-zero values
    const contributorsWithMetrics = contributors
      .filter((value) => value.valueToUse > 0)
      .map((value) => {
        // For UI purposes - equal heights when all values are 0
        const heightPercentage =
          totalValueToUse === 0
            ? 100 / contributors.length
            : (value.valueToUse / totalValueToUse) * 100;

        // For display percentage
        const displayPercentage =
          value.valueToUse === 0
            ? 0
            : (value.valueToUse / totalValueToUse) * 100;

        return {
          ...value,
          displayPercentage,
          displayHeight: Math.max(
            (heightPercentage / 100) * CONTAINER_HEIGHT_PX,
            MIN_BAR_HEIGHT_PX
          )
        };
      });

    // If all contributors have zero values
    if (contributorsWithMetrics.length === 0) {
      return (
        <div className="text-center py-4">No non-zero data to display</div>
      );
    }

    // Adjust heights to fit container if minimum heights exceed container height
    const totalMinHeight = contributorsWithMetrics.length * MIN_BAR_HEIGHT_PX;
    const scaleFactor =
      totalMinHeight > CONTAINER_HEIGHT_PX
        ? CONTAINER_HEIGHT_PX / totalMinHeight
        : 1;

    return (
      <div className="flex gap-3 h-48">
        <div className="flex flex-col h-full justify-between gap-1">
          {contributorsWithMetrics.reverse().map((value, index) => (
            <div
              key={value.user?.id ?? value.userId ?? Date.now()}
              className="w-full"
              style={{
                height: `${value.displayHeight * scaleFactor}px`,
                minHeight: `${MIN_BAR_HEIGHT_PX * scaleFactor}px`
              }}
            >
              <div className="w-full h-full items-center flex flex-row">
                <div
                  className="w-2 h-full rounded-[2px]"
                  style={{
                    background:
                      salesPipelineColors[avatarColorIndex(index)].stroke
                  }}
                />
                <div className="flex flex-col w-full ml-2">
                  <div className="font-normal">{value.user?.displayName}</div>
                  <div className="flex gap-4">
                    <div className="text-lg leading-4">
                      {displayTotalCurrency(value.valueToUse, true)}
                    </div>
                    <div className="leading-5 text-h3">
                      {`${reducePrecision(value.displayPercentage)}%`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [data, isProbabilityMode, stageContributorValue, index, stagesData]);

  if (!stagesData?.length || !data) return trigger;

  return (
    <Tooltip>
      <TooltipTrigger className="w-fit p-2 rounded-lg hover:bg-surface-tooltip-toast">
        {trigger}
      </TooltipTrigger>
      <TooltipContent
        data-test="MRR-tooltip"
        side="right"
        sideOffset={10}
        className="min-w-80 w-fit min-h-72 h-fit flex flex-col gap-5 p-5"
      >
        <div className="flex justify-between space-x-4 pb-4 items-center">
          <Text
            as="div"
            variant="body"
          >{`${data.stageName} ${arrMrrLabel}`}</Text>
          {/* Temporarily removed */}
          {/* <span className="text-h3">
            <FileDown />
          </span> */}
        </div>
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  );
};

export default MRRTooltip;
